import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import {
  colorActionPrimary,
  colorActionPrimaryText,
  colorBorder,
  colorBorderHover,
  colorSurface,
  colorSurfaceHover,
  colorTextMuted,
  colorTextPrimary,
  colorDealBg,
  colorDealBorder,
  colorDealText,
  colorDealAccent,
  radiusCard,
  radiusInput,
  shadowCard,
} from "src/design/tokens";
import { PublicDeal } from "src/data/clientBooking/queries/clientBooking";
import { formatTime12h, getDealForSlot } from "../utils";

interface Props {
  selectedDate: Dayjs;
  morningSlots: string[];
  eveningSlots: string[];
  applicableDeals: PublicDeal[];
  onSelectTime: (time: string, deal: PublicDeal | null) => void;
  onBack: () => void;
}

const TimeSlotButton = ({
  time,
  hasDeal,
  isSelected,
  onClick,
}: {
  time: string;
  hasDeal: boolean;
  isSelected: boolean;
  onClick: () => void;
}) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        border: isSelected
          ? `2px solid ${colorDealAccent}`
          : `1px solid ${colorBorder}`,
        borderRadius: radiusInput,
        p: 1.25,
        cursor: "pointer",
        backgroundColor: isSelected
          ? colorActionPrimary
          : hasDeal
            ? colorDealBg
            : colorSurface,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 0.8,
        width: "100%",
        transition: "all 0.15s",
        "&:hover": {
          borderColor: colorBorderHover,
          backgroundColor: isSelected
            ? colorActionPrimary
            : hasDeal
              ? colorDealBg
              : colorSurfaceHover,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        },
      }}
    >
      {hasDeal && (
        <LocalOfferOutlinedIcon
          sx={{
            fontSize: 12,
            color: isSelected ? colorActionPrimaryText : colorDealText,
          }}
        />
      )}
      <Typography
        variant="body2"
        fontWeight={500}
        color={isSelected ? colorActionPrimaryText : colorTextPrimary}
      >
        {formatTime12h(time)}
      </Typography>
    </Box>
  );
};

const SlotGrid = ({
  slots,
  applicableDeals,
  pendingTime,
  onSlotClick,
}: {
  slots: string[];
  applicableDeals: PublicDeal[];
  pendingTime: string | null;
  onSlotClick: (time: string) => void;
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 1,
    }}
  >
    {slots.map((time) => {
      const deal = getDealForSlot(time, applicableDeals);
      return (
        <TimeSlotButton
          key={time}
          time={time}
          hasDeal={!!deal}
          isSelected={pendingTime === time}
          onClick={() => onSlotClick(time)}
        />
      );
    })}
  </Box>
);

const TimeSelection = ({
  selectedDate,
  morningSlots,
  eveningSlots,
  applicableDeals,
  onSelectTime,
  onBack,
}: Props) => {
  const { t } = useTranslation();
  const [pendingTime, setPendingTime] = useState<string | null>(null);

  const formattedDate = selectedDate.format("dddd, MMMM D, YYYY");

  const selectedDeal = pendingTime
    ? getDealForSlot(pendingTime, applicableDeals)
    : null;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 1.5,
          cursor: "pointer",
          width: "fit-content",
          color: colorTextMuted,
          "&:hover": { color: colorTextPrimary },
        }}
        onClick={onBack}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" fontWeight={500}>
          {t("clientBooking.back")}
        </Typography>
      </Box>

      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        {t("clientBooking.selectTime")}
      </Typography>
      <Typography variant="body2" color={colorTextMuted} sx={{ mb: 2.5 }}>
        {formattedDate}
      </Typography>

      {morningSlots.length > 0 && (
        <Box
          sx={{
            backgroundColor: colorSurface,
            border: `1px solid ${colorBorder}`,
            borderRadius: radiusCard,
            boxShadow: shadowCard,
            p: 2,
            mb: eveningSlots.length > 0 ? 2 : 0,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            {t("clientBooking.morning")}
          </Typography>
          <SlotGrid
            slots={morningSlots}
            applicableDeals={applicableDeals}
            pendingTime={pendingTime}
            onSlotClick={setPendingTime}
          />
        </Box>
      )}

      {eveningSlots.length > 0 && (
        <Box
          sx={{
            backgroundColor: colorSurface,
            border: `1px solid ${colorBorder}`,
            borderRadius: radiusCard,
            boxShadow: shadowCard,
            p: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            {t("clientBooking.evening")}
          </Typography>
          <SlotGrid
            slots={eveningSlots}
            applicableDeals={applicableDeals}
            pendingTime={pendingTime}
            onSlotClick={setPendingTime}
          />
        </Box>
      )}

      {selectedDeal && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            backgroundColor: colorDealBg,
            border: `1px solid ${colorDealBorder}`,
            borderRadius: radiusCard,
            p: 1.5,
          }}
        >
          <LocalOfferOutlinedIcon
            sx={{ fontSize: 16, color: colorDealText, mt: 0.1, flexShrink: 0 }}
          />
          <Typography variant="body2" color={colorDealText}>
            {selectedDeal.description}
          </Typography>
        </Box>
      )}

      {pendingTime && (
        <Button
          variant="contained"
          fullWidth
          onClick={() => onSelectTime(pendingTime, selectedDeal)}
          sx={{ mt: 2 }}
        >
          {t("clientBooking.continueCta")}
        </Button>
      )}
    </Box>
  );
};

export default TimeSelection;
